import { UserSession } from '@allocab/allocab-commons-shared-types';
import {
  ArrowRightFromBracketSolid,
  BarsSolid,
  CalendarDaysSolid,
  CarSolid,
  ChevronDownSolid,
  ChevronRightSolid,
  CitySolid,
  ClockRotateLeftSolid,
  FileInvoiceDollarSolid,
  GearSolid,
  RouteSolid,
  UsersSolid,
  XmarkSolid,
} from '@allocab/allocab-icons';
import Intercom from '@intercom/messenger-js-sdk';
import jwtDecode from 'jwt-decode';
import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import type { NavLinkProps } from 'react-router-dom';
import { twJoin, twMerge } from 'tailwind-merge';

import { LogoutModal } from './LogoutModal';
import allocabLogo from '../../../assets/images/allocab-logo.svg';
import { useFetchAccountDocuments } from '../../../hooks/useFetchAccountDocument';
import { useFetchUser } from '../../../hooks/useFetchUser';
import { useTokens } from '../../../hooks/useTokens';
import { t } from '../../../services/translation/translation';
import { getFullName } from '../../../utils/common';
import { getDocumentItemStatus } from '../../../utils/document';
import { Avatar } from '../../Avatar';
import { CubeIcon } from '../../icons/CubeIcon';
import { TableListIcon } from '../../icons/TableListIcon';
import { TransfertIcon } from '../../icons/TransfertIcon';
import { useFetchIntercom } from '../../intercom/hooks/useFetchIntercom';

const navLinkStyles: NavLinkProps['className'] = ({ isActive }) =>
  twJoin(
    'w-full flex items-center p-2 text-gray-900 fill-gray-900 rounded-lg dark:text-white dark:fill-white hover:bg-gray-100 dark:hover:bg-gray-700 group',
    isActive && 'bg-gray-100 dark:bg-gray-700',
  );

export const SideBar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [expandedSection, setExpandedSection] = useState<'trips' | 'invoicing' | undefined>(undefined);
  const [expandedSubSection, setExpandedSubSection] = useState<'invoicing-legacy' | undefined>(undefined);
  const location = useLocation();
  const { data } = useFetchAccountDocuments();
  const { tokens } = useTokens();
  const session = tokens ? (jwtDecode(tokens.accessToken) as UserSession) : null;
  const { data: intercom } = useFetchIntercom();

  const nbInvalidDocuments =
    data?.items.filter(d => ['REJECTED', 'TO_DEFINE'].includes(getDocumentItemStatus(d))).length ?? 0;

  const { data: user } = useFetchUser();

  useEffect(() => {
    if (intercom && session && user) {
      Intercom({
        app_id: 'sguwf3h2',
        region: 'eu',
        user_hash: intercom.web,
        user_id: session.userTypeKeyStr,
        name: getFullName(user.firstName, user.lastName),
        email: user.email,
      });
    }
  }, [intercom, session, user]);

  useEffect(() => {
    if (location.pathname.startsWith('/invoicing')) {
      setExpandedSection('invoicing');
      if (location.pathname.startsWith('/invoicing/fortnight') || location.pathname.startsWith('/invoicing/month')) {
        setExpandedSubSection('invoicing-legacy');
      }
    } else if (location.pathname.startsWith('/trips')) {
      setExpandedSection('trips');
    }
    setIsOpen(false);
  }, [location.pathname]);

  return (
    <div className="flex flex-row">
      <button
        onClick={() => setIsOpen(true)}
        type="button"
        className="inline-flex items-center p-2 mt-2 ml-3 rounded-lg sm:hidden "
      >
        <BarsSolid className="fill-current" width={27} height={27} />
      </button>
      <aside
        className={twMerge(
          'fixed top-0 left-0 z-40 w-64 h-screen transition-transform overflow-hidden bg-gray-900 bg-opacity-25 inset-0 transform ease-in-out -translate-x-full sm:translate-x-0',
          isOpen && '-translate-x-0',
        )}
        aria-label="Sidebar"
      >
        <div className="h-full flex flex-col px-3 py-4 overflow-y-auto bg-gray-50 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700">
          <div className="flex flex-row">
            <a href="https://www.allocab.com" className="flex items-start pl-2.5 mb-5">
              <img className="h-8 w-auto" src={allocabLogo} alt="allocab" />
            </a>
            <div className="ml-10 visible sm:invisible">
              <button
                onClick={() => setIsOpen(false)}
                type="button"
                className=" bg-gray-100  dark:bg-gray-600 focus:ring-4 focus:outline-none font-medium rounded-full text-sm p-2.5 text-center inline-flex items-center me-2"
              >
                <XmarkSolid className="fill-current" width={12} height={12} />
              </button>
            </div>
          </div>
          <div className="space-y-2 flex-1">
            <Avatar />
            <div className="border w-full border-gray-100 dark:border-gray-600" />
            <ul className="space-y-2 font-medium">
              <li>
                <button
                  type="button"
                  className={navLinkStyles({ isActive: false, isPending: false })}
                  onClick={() => setExpandedSection(v => (v === 'trips' ? undefined : 'trips'))}
                >
                  <RouteSolid width={16} height={16} />
                  <span className="ml-3 flex-1 text-start">{t('side-bar.trips')}</span>
                  {expandedSection === 'trips' ? (
                    <ChevronDownSolid width={12} height={12} />
                  ) : (
                    <ChevronRightSolid width={12} height={12} />
                  )}
                </button>

                <ul
                  id="dropdown-example"
                  className={twMerge('py-2 space-y-2', expandedSection !== 'trips' && 'hidden')}
                >
                  <li className="ml-7">
                    <NavLink to="/trips/list?state=INCOMING" className={navLinkStyles} end>
                      <TableListIcon className="w-4 h-4" />
                      <span className="ml-3 flex-1 text-start">{t('side-bar.trips-list')}</span>
                    </NavLink>
                  </li>
                  <li className="ml-7">
                    <NavLink to="/trips/calendar" className={navLinkStyles} end>
                      <CalendarDaysSolid width={16} height={16} />
                      <span className="ml-3 flex-1 text-start">{t('side-bar.trips-calendar')}</span>
                    </NavLink>
                  </li>
                  <li className="ml-7">
                    <NavLink to="/trips/packages" className={navLinkStyles} end>
                      <CubeIcon className="w-4 h-4" />
                      <span className="ml-3 flex-1 text-start">{t('side-bar.trips-packages')}</span>
                    </NavLink>
                  </li>
                </ul>
              </li>
              <li>
                <button
                  type="button"
                  className={navLinkStyles({ isActive: false, isPending: false })}
                  onClick={() => setExpandedSection(v => (v === 'invoicing' ? undefined : 'invoicing'))}
                >
                  <FileInvoiceDollarSolid width={16} height={16} />
                  <span className="ml-3 flex-1 text-start">{t('side-bar.invoicing')}</span>
                  {expandedSection === 'invoicing' ? (
                    <ChevronDownSolid width={12} height={12} />
                  ) : (
                    <ChevronRightSolid width={12} height={12} />
                  )}
                </button>

                <ul className={twMerge('py-2 space-y-2', expandedSection !== 'invoicing' && 'hidden')}>
                  <li className="ml-7">
                    <NavLink to="/invoicing/invoices" className={navLinkStyles} end>
                      <FileInvoiceDollarSolid width={16} height={16} />
                      <span className="ml-3 flex-1 text-start">{t('side-bar.invoices')}</span>
                    </NavLink>
                  </li>
                  <li className="ml-7">
                    <NavLink to="/invoicing/transactions" className={navLinkStyles} end>
                      <TransfertIcon className="w-4 h-4" />
                      <span className="ml-3 flex-1 text-start">{t('side-bar.transactions')}</span>
                    </NavLink>
                  </li>
                  <li className="ml-7">
                    <NavLink to="/invoicing/fleet" className={navLinkStyles} end>
                      <UsersSolid width={16} height={16} />
                      <span className="ml-3 flex-1 text-start">{t('side-bar.fleet')}</span>
                    </NavLink>
                  </li>
                  <li className="ml-7">
                    <button
                      type="button"
                      className={navLinkStyles({ isActive: false, isPending: false })}
                      onClick={() =>
                        setExpandedSubSection(v => (v === 'invoicing-legacy' ? undefined : 'invoicing-legacy'))
                      }
                    >
                      <ClockRotateLeftSolid width={16} height={16} />
                      <span className="ml-3 flex-1 text-start">{t('side-bar.before-2025')}</span>
                      {expandedSubSection === 'invoicing-legacy' ? (
                        <ChevronDownSolid width={12} height={12} />
                      ) : (
                        <ChevronRightSolid width={12} height={12} />
                      )}
                    </button>

                    <ul className={twMerge('py-2 space-y-2', expandedSubSection !== 'invoicing-legacy' && 'hidden')}>
                      <li className="ml-7">
                        <NavLink to="/invoicing/fortnight" className={navLinkStyles} end>
                          <CalendarDaysSolid width={16} height={16} />
                          <span className="ml-3 flex-1 text-start">{t('side-bar.invoicing-fortnight')}</span>
                        </NavLink>
                      </li>
                      <li className="ml-7">
                        <NavLink to="/invoicing/month" className={navLinkStyles} end>
                          <CalendarDaysSolid width={16} height={16} />
                          <span className="ml-3 flex-1 text-start">{t('side-bar.invoicing-month')}</span>
                        </NavLink>
                      </li>
                    </ul>
                  </li>
                </ul>
              </li>
              <li>
                <NavLink to="/vehicles" className={navLinkStyles} end>
                  <CarSolid width={16} height={16} />
                  <span className="ml-3">{t('side-bar.vehicles')}</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/drivers" className={navLinkStyles} end>
                  <UsersSolid width={16} height={16} />
                  <span className="ml-3">{t('side-bar.drivers')}</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/company" className={navLinkStyles} end>
                  <CitySolid width={16} height={16} />
                  <span className="ml-3 flex-1">{t('common.company')}</span>
                  {nbInvalidDocuments > 0 ? <span className="flex w-2 h-2 bg-red-500 rounded-full " /> : null}
                </NavLink>
              </li>
            </ul>
            <div className="border w-full border-gray-100 dark:border-gray-600" />
            <ul className="space-y-2 font-medium">
              <li>
                <NavLink to="/settings" className={navLinkStyles} end>
                  <GearSolid width={16} height={16} />
                  <span className="ml-3">{t('side-bar.settings')}</span>
                </NavLink>
              </li>
              <li>
                <button
                  type="button"
                  className="w-full flex items-center p-2 text-gray-900 fill-gray-900 rounded-lg dark:text-white dark:fill-white hover:bg-gray-100 dark:hover:bg-gray-700 group"
                  onClick={() => setIsModalVisible(true)}
                >
                  <ArrowRightFromBracketSolid width={16} height={16} />
                  <span className="ml-3">{t('side-bar.logout')}</span>
                </button>
                {isModalVisible ? <LogoutModal onClose={() => setIsModalVisible(false)} /> : null}
              </li>
            </ul>
          </div>
        </div>
      </aside>
      {isOpen ? (
        <div
          onClick={() => setIsOpen(false)}
          className={
            'fixed overflow-hidden  bg-gray-50 dark:bg-gray-800 bg-opacity-25 dark:bg-opacity-25 inset-0 transform ease-in-out transition-opacity opacity-100 duration-500 -translate-x-0 z-10 cursor-pointer'
          }
        ></div>
      ) : null}
    </div>
  );
};
